import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Activity, MapPin, ShieldCheck } from 'lucide-react';
export const Technology: React.FC = () => {
  return <section id="technology" className="py-32 bg-surface" data-id="element-551">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-552">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-553">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-554">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-mint text-leaf text-sm font-mono mb-6" data-id="element-555">
              <Cpu className="w-4 h-4" data-id="element-556" /> Connected Assets
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-primary mb-6" data-id="element-557">
              Every asset, accounted for.
            </h2>
            <p className="text-lg text-ink/70 mb-8 leading-relaxed" data-id="element-558">
              Every bottle, crate, and pallet we own is tracked. We know where
              it is, how many trips it's done, and what shape it's in. That
              means we keep things in use longer and catch problems before they
              cost you.
            </p>

            <ul className="space-y-6" data-id="element-559">
              {[{
              icon: <Activity data-id="element-560" />,
              title: "How many trips it's done",
              desc: "See exactly how many cycles an asset has racked up and when it's due for a check-up."
            }, {
              icon: <MapPin data-id="element-561" />,
              title: "Where it's been",
              desc: 'Keep an eye on where things are headed and how reliably they come back, by region and by customer.'
            }, {
              icon: <ShieldCheck data-id="element-562" />,
              title: "What shape it's in",
              desc: 'Every asset gets checked before it goes back out, so you only ever get the good stuff.'
            }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-563">
                  <div className="text-leaf mt-1" data-id="element-564">{item.icon}</div>
                  <div data-id="element-565">
                    <h4 className="font-medium text-primary mb-1" data-id="element-566">
                      {item.title}
                    </h4>
                    <p className="text-sm text-ink/70" data-id="element-567">{item.desc}</p>
                  </div>
                </li>)}
            </ul>
          </motion.div>

          {/* Dashboard Mockup */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-primary rounded-2xl p-6 md:p-8 shadow-glossy-lg text-white relative overflow-hidden gloss-dark" data-id="element-568">
            {/* Decorative background grid */}
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(var(--color-surface) 1px, transparent 1px)',
            backgroundSize: '24px 24px'
          }} data-id="element-569" />

            <div className="relative z-10" data-id="element-570">
              <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-6" data-id="element-571">
                <div className="font-mono text-sm opacity-70" data-id="element-572">
                  TELEMETRY_DASHBOARD
                </div>
                <div className="flex gap-2" data-id="element-573">
                  <div className="w-3 h-3 rounded-full bg-white/20" data-id="element-574" />
                  <div className="w-3 h-3 rounded-full bg-white/20" data-id="element-575" />
                  <div className="w-3 h-3 rounded-full bg-leaf" data-id="element-576" />
                </div>
              </div>

              <div className="space-y-6" data-id="element-577">
                <div className="bg-white/5 rounded-lg p-4 border border-white/10" data-id="element-578">
                  <div className="flex justify-between items-start mb-4" data-id="element-579">
                    <div data-id="element-580">
                      <div className="font-mono text-xs text-leaf mb-1" data-id="element-581">
                        ASSET_ID
                      </div>
                      <div className="font-mono text-xl" data-id="element-582">CYB-8492-A</div>
                    </div>
                    <div className="text-right" data-id="element-583">
                      <div className="font-mono text-xs text-leaf mb-1" data-id="element-584">
                        STATUS
                      </div>
                      <div className="inline-flex items-center gap-2 text-sm" data-id="element-585">
                        <span className="w-2 h-2 rounded-full bg-leaf animate-pulse" data-id="element-586" />{' '}
                        ACTIVE
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10" data-id="element-587">
                    <div data-id="element-588">
                      <div className="font-mono text-xs opacity-50 mb-1" data-id="element-589">
                        CYCLE_COUNT
                      </div>
                      <div className="font-mono text-lg" data-id="element-590">14 / 50</div>
                    </div>
                    <div data-id="element-591">
                      <div className="font-mono text-xs opacity-50 mb-1" data-id="element-592">
                        CONDITION_SCORE
                      </div>
                      <div className="font-mono text-lg text-leaf" data-id="element-593">98.2%</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-lg p-4 border border-white/10" data-id="element-594">
                  <div className="font-mono text-xs text-leaf mb-3" data-id="element-595">
                    RECENT_ACTIVITY
                  </div>
                  <div className="space-y-3 font-mono text-sm opacity-80" data-id="element-596">
                    <div className="flex justify-between" data-id="element-597">
                      <span data-id="element-598">[10:42:01] SANITIZED</span>
                      <span data-id="element-599">FACILITY_02</span>
                    </div>
                    <div className="flex justify-between" data-id="element-600">
                      <span data-id="element-601">[08:15:33] RETURN_SCANNED</span>
                      <span data-id="element-602">DROP_PT_88</span>
                    </div>
                    <div className="flex justify-between opacity-50" data-id="element-603">
                      <span data-id="element-604">[YEST] DISTRIBUTED</span>
                      <span data-id="element-605">BREWERY_AX</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};